// May this side act at all right now?
//
// **One question, one answer, two readers** (§29 U1). The board's own gate
// (`useBoardInteraction`'s `controlsTurn`) and the inspector's (`selection.ts`'s
// `settable`) used to derive this separately, and drifted: policy controls
// stayed lit on the opponent's turn because the card's gate said something the
// board's did not. A button that is shown and then does nothing is the failure
// this codebase is written against, so both call this.
//
// It mirrors `requireBuilder` in `src/engine/rules/reduce.ts`, which is the authority
// — this is the client catching up with it, exactly as the seat gate is.
//
// Pure: no React, no DOM. Its own file rather than a line in either caller,
// because the whole point is that neither owns it.

import type { Color } from "src/engine/rules/board";
import type { GameState } from "src/engine/rules/commands";

/**
 * Why this side may not act, or null when it may.
 *
 * Two reasons and they are different in kind. **The clock** is the rule
 * (§29 U1), mirroring `requireBuilder`. **The screen** is not a rule at all
 * (§29 U4): a player looking back through history is looking at a board the
 * game has moved on from, so a command issued against it would land on a
 * position they cannot see. The reducer would take it; the honest answer is
 * that they meant it about the board in front of them.
 *
 * The screen answers first, because it is the one the player can see they did.
 */
export function actRefusal(
  state: GameState,
  side: Color,
  viewing = false
): ActBlocked | null {
  if (viewing) return "viewing-history";
  return state.position.turn === side ? null : "not-your-turn";
}

/** Why a click was refused, keyed into `REFUSAL`. */
export type ActBlocked = "not-your-turn" | "viewing-history";

/**
 * The side on the clock is the only side that may act, and only on the board
 * it is standing on.
 *
 * `localSide` is the seat you occupy (`seatedAs`), never the piece's colour —
 * seated as Black on White's turn, a White piece is still not yours to move.
 */
export function canAct(state: GameState, side: Color, viewing = false): boolean {
  return actRefusal(state, side, viewing) === null;
}
